import { CircularProgress, Stack, Typography } from "@mui/material";
import type { UseQueryResult } from "@tanstack/react-query";
import { Card } from "~/components/Card";
import { Center } from "~/components/Center";
import {
  DetailsLayout,
  LockCard,
  RelatedResource,
} from "~/components/DetailsCards";
import {
  OBJECT_KEY_DELIMITER,
  ObjectSearchRequestProvider,
} from "~/components/ObjectExplorer";
import { QueryRenderer } from "~/components/QueryRenderer";
import { ErrorMessage } from "~/components/error-message";
import type { Digestion } from "~/lqs";
import {
  EditLqsResourceForm,
  LqsHistoryCard,
  LqsManageCard,
  LqsResourceFields,
  useDeleteDigestion,
  useDigestion,
  useUpdateDigestion,
} from "~/lqs";
import { calculateProcessRefetchInterval, Process } from "~/lqs/process";
import { lqsRoutePaths, useLqsNavigator, useTypedParams } from "~/paths";
import { selectData } from "~/utils";
import { LogObjectTable } from "../../logs/components/LogObjectTable";
import { editDigestionSchema } from "../schemas";
import {
  DigestionPartSearchRequestProvider,
  DigestionPartTable,
} from "./DigestionPartTable";
import {
  DigestionTopicSearchRequestProvider,
  DigestionTopicTable,
} from "./DigestionTopicTable";

export function DigestionDetails() {
  const { digestionId } = useTypedParams(lqsRoutePaths.DIGESTION_DETAILS);

  const lqsNavigator = useLqsNavigator({
    toDigestionTopicTable: true,
    toDigestionPartTable: true,
    toDigestionEdit: true,
    toDigestionTable: true,
  });

  const query = useDigestion(digestionId, {
    select: selectData,
    refetchInterval: calculateProcessRefetchInterval,
  });

  const generalSection = <GeneralSection query={query} />;
  const processSection = (
    <Process query={query} mutation={useUpdateDigestion(digestionId)} />
  );
  const relatedResourcesSection = (
    <Stack spacing={2}>
      <Typography variant="h4" component="h2">
        Related Resources
      </Typography>
      <RelatedObjects query={query} />
      <RelatedResource
        text="Topics"
        to={lqsNavigator.toDigestionTopicTable({ digestionId })}
        table={
          <DigestionTopicSearchRequestProvider embedded>
            <DigestionTopicTable />
          </DigestionTopicSearchRequestProvider>
        }
      />
      <RelatedResource
        text="Parts"
        to={lqsNavigator.toDigestionPartTable({ digestionId })}
        table={
          <DigestionPartSearchRequestProvider embedded>
            <DigestionPartTable />
          </DigestionPartSearchRequestProvider>
        }
      />
    </Stack>
  );
  const infoSection = <InfoSection query={query} />;
  const lockSection = (
    <LockCard
      resourceName="digestion"
      query={query}
      updateMutation={useUpdateDigestion(digestionId)}
    />
  );
  const historySection = <LqsHistoryCard query={query} />;
  const manageSection = (
    <LqsManageCard
      resourceName="digestion"
      query={query}
      editLocation={lqsNavigator.toDigestionEdit({ digestionId })}
      deleteMutation={useDeleteDigestion(digestionId)}
      getReadableName={(digestion) => digestion.name ?? digestion.id}
      listLocation={lqsNavigator.toDigestionTable()}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {processSection}
          {relatedResourcesSection}
        </>
      }
      secondaryGridColumn={
        <>
          {infoSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {infoSection}
          {processSection}
          {lockSection}
          {historySection}
          {manageSection}
          {relatedResourcesSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<Digestion> }) {
  return (
    <Card title="General">
      <LqsResourceFields
        registryKey="digestion"
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          { dataType: "foreign-key", resourceType: "log", accessor: "logId" },
          {
            dataType: "foreign-key",
            resourceType: "workflow",
            accessor: "workflowId",
          },
          { dataType: "json", accessor: "workflowContext" },
          { dataType: "json", accessor: "context" },
        ]}
      />
    </Card>
  );
}

function RelatedObjects({ query }: { query: UseQueryResult<Digestion> }) {
  const lqsNavigator = useLqsNavigator({ toLogObjectTable: true });

  return (
    <QueryRenderer
      query={query}
      loading={
        <Card>
          <Center sx={{ height: 350 }}>
            <CircularProgress />
          </Center>
        </Card>
      }
      error={
        <Card>
          <ErrorMessage>Couldn't load objects</ErrorMessage>
        </Card>
      }
      success={(digestion) => {
        const digestionPrefix = ["digestions", digestion.id, ""].join(
          OBJECT_KEY_DELIMITER,
        );

        return (
          <RelatedResource
            text="Objects"
            to={lqsNavigator.toLogObjectTable(
              { logId: digestion.logId },
              { directory: digestionPrefix },
            )}
            table={
              <ObjectSearchRequestProvider embedded>
                <LogObjectTable
                  logId={digestion.logId}
                  homeName={digestion.id}
                  subResourcePrefix={digestionPrefix}
                />
              </ObjectSearchRequestProvider>
            }
          />
        );
      }}
    />
  );
}

function InfoSection({ query }: { query: UseQueryResult<Digestion> }) {
  const { digestionId } = useTypedParams(lqsRoutePaths.DIGESTION_DETAILS);

  return (
    <EditLqsResourceForm
      resourceName="digestion"
      registryKey="digestion"
      disabled={(digestion) => digestion.locked}
      footer={(digestion) =>
        digestion.locked && (
          <Typography variant="body2">
            Unlock this digestion to make updates.
          </Typography>
        )
      }
      query={query}
      schema={editDigestionSchema}
      descriptors={[
        { name: "name", type: "text" },
        { name: "note", type: "text", multiline: true },
      ]}
      mutation={useUpdateDigestion(digestionId)}
    />
  );
}
